import React, { useState, useEffect } from 'react';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import Modal from './common/Modal';

interface RateModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessName: string;
  onSubmit: (rating: number) => Promise<void>;
}

const RateModal: React.FC<RateModalProps> = ({ isOpen, onClose, businessName, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRating(0);
      setSubmitStatus('idle');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (rating === 0) return;
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      await onSubmit(rating);
      setSubmitStatus('success');
      // Automatically close after a short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setRating(0);
    setSubmitStatus('idle');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background p-6 rounded-lg w-full max-w-md mx-4">
        <h2 className="text-xl font-semibold text-white mb-4">
          Avaliar {businessName}
        </h2>

        {/* Stars Rating */}
        {submitStatus !== 'success' && (
          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="focus:outline-none"
              >
                {star <= rating ? (
                  <StarIconSolid className="h-8 w-8 text-yellow-400" />
                ) : (
                  <StarIcon className="h-8 w-8 text-yellow-400" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Status Message */}
        {submitStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-500/20 text-green-400 rounded-lg text-center">
            <p>Avaliação enviada com sucesso!</p>
          </div>
        )}
        {submitStatus === 'error' && (
          <div className="mb-4 p-3 bg-red-500/20 text-red-400 rounded-lg text-center">
            Erro ao enviar avaliação. Tente novamente.
          </div>
        )}

        {/* Buttons */}
        {submitStatus !== 'success' && (
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 py-2 px-4 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={rating === 0 || isSubmitting}
              className="flex-1 py-2 px-4 rounded-lg bg-primary text-white hover:bg-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RateModal;
